import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/NumberInput";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { CodecPolicyEditor } from "@/components/CodecPolicyEditor";
import { BusinessTypeCombobox } from "@/components/BusinessTypeCombobox";
import { Plus, Trash2, PhoneIncoming, Star, StarOff, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * SectionHeading — Phase 6m.
 *
 * Small-caps, letter-spaced heading above a grouped set of form fields.
 * This is the Vodia-borrowed pattern from the design guidelines
 * (§2 Typography.section_heading and §8 Form Pattern.sectioning) that
 * breaks long forms into scannable chunks without adding visual weight.
 * Pair it with a muted `<Separator>` for the 3CX "ruled section" feel.
 */
export function SectionHeading({ children, testid }) {
  return (
    <div
      className="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase"
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/**
 * StatusSquare — Phase 6m.
 *
 * 3CX-style 8px square status indicator. Used in editor sticky headers
 * and row listings to telegraph registered / degraded / failed state
 * without leaning on colored text. Tooltip-capable via `title`.
 */
export function StatusSquare({ kind = "offline", title, className = "" }) {
  const bg = ({
    ok: "bg-[hsl(var(--ok))]",
    warn: "bg-[hsl(var(--warn))]",
    danger: "bg-[hsl(var(--danger))]",
    info: "bg-[hsl(var(--info))]",
    offline: "bg-[hsl(var(--offline))]",
    security: "bg-[hsl(var(--security))]",
  })[kind] || "bg-[hsl(var(--offline))]";
  return (
    <span
      aria-label={title || `status-${kind}`}
      title={title}
      className={`inline-block w-2 h-2 rounded-[2px] ${bg} ${className}`.trim()}
    />
  );
}

function normalizeDigits(s) {
  return String(s || "").replace(/[^0-9]/g, "");
}

export function DidsEditor({
  dids = [],
  defaultCid = "",
  passthroughFarEndCid = false,
  onChange,
}) {
  const { t } = useTranslation();
  const [nextValue, setNextValue] = useState("");

  const emit = (next) => {
    onChange &&
      onChange({
        dids: next.dids ?? dids,
        default_cid: next.default_cid ?? defaultCid,
        passthrough_far_end_cid:
          next.passthrough_far_end_cid ?? passthroughFarEndCid,
      });
  };

  const add = () => {
    const v = nextValue.trim();
    if (!v) return;
    const exists = dids.some((d) => normalizeDigits(d) === normalizeDigits(v));
    if (exists) {
      toast.error(t("pages.customers.didExists"));
      return;
    }
    setNextValue("");
    emit({ dids: [...dids, v] });
  };

  const remove = (d) => {
    const nextDids = dids.filter((x) => x !== d);
    let nextDefault = defaultCid;
    if (normalizeDigits(defaultCid) === normalizeDigits(d)) {
      nextDefault = "";
    }
    emit({ dids: nextDids, default_cid: nextDefault });
  };

  const setDefault = (d) => {
    if (normalizeDigits(defaultCid) === normalizeDigits(d)) {
      // Toggle off → no default → behavior depends on passthrough checkbox.
      emit({ default_cid: "" });
    } else {
      emit({ default_cid: d });
    }
  };

  // Live status banner — explains the EFFECTIVE outbound behaviour for
  // the current combination of (default_cid, passthrough_far_end_cid).
  let mode, modeKind, modeText;
  if (defaultCid && !passthroughFarEndCid) {
    mode = t("pages.customers.cidMode.rewrite");
    modeKind = "ok";
    modeText = t("pages.customers.cidMode.rewriteHint", { cid: defaultCid });
  } else if (defaultCid && passthroughFarEndCid) {
    mode = t("pages.customers.cidMode.hybrid");
    modeKind = "info";
    modeText = t("pages.customers.cidMode.hybridHint", { cid: defaultCid });
  } else if (!defaultCid && passthroughFarEndCid) {
    mode = t("pages.customers.cidMode.passthrough");
    modeKind = "info";
    modeText = t("pages.customers.cidMode.passthroughHint");
  } else {
    mode = t("pages.customers.cidMode.reject");
    modeKind = "warn";
    modeText = t("pages.customers.cidMode.rejectHint");
  }
  const modeColor = {
    ok: "border-[hsl(var(--ok))]/40 bg-[hsl(var(--ok))]/10 text-[hsl(var(--ok))]",
    info: "border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
    warn: "border-[hsl(var(--warn))]/40 bg-[hsl(var(--warn))]/10 text-[hsl(var(--warn))]",
  }[modeKind];

  return (
    <div className="rounded-md border border-border p-3 space-y-3" data-testid="dids-editor">
      <div className="flex items-center gap-2 text-sm font-medium">
        <PhoneIncoming className="h-4 w-4 text-[hsl(var(--accent))]" />
        {t("pages.customers.didsTitle")}
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        {t("pages.customers.didsHelp")}
      </p>

      {/* Effective-mode banner */}
      <div
        className={`rounded-md border px-3 py-2 text-[11px] flex items-start gap-2 ${modeColor}`}
        data-testid="cid-mode-banner"
      >
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold uppercase tracking-wide">{mode}</div>
          <div className="text-foreground/80 mt-0.5">{modeText}</div>
        </div>
      </div>

      {/* Passthrough toggle */}
      <label
        className="flex items-start gap-2 text-xs cursor-pointer select-none rounded-md border border-border/60 px-2 py-2 hover:bg-muted/40"
        data-testid="passthrough-far-end-cid-row"
      >
        <input
          type="checkbox"
          className="mt-0.5 accent-[hsl(var(--accent))]"
          checked={!!passthroughFarEndCid}
          onChange={(e) => emit({ passthrough_far_end_cid: e.target.checked })}
          data-testid="passthrough-far-end-cid"
        />
        <span>
          <span className="font-medium text-foreground">
            {t("pages.customers.passthroughLabel")}
          </span>
          <span className="block text-muted-foreground mt-0.5">
            {t("pages.customers.passthroughHelp")}
          </span>
        </span>
      </label>

      {/* DID list */}
      <div className="flex gap-2">
        <Input
          placeholder="+14155551234"
          value={nextValue}
          onChange={(e) => setNextValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          data-testid="did-input"
        />
        <Button type="button" size="sm" onClick={add} data-testid="did-add">
          <Plus className="h-4 w-4 mr-1" />
          {t("common.add")}
        </Button>
      </div>
      {dids.length === 0 ? (
        <div className="text-xs italic text-muted-foreground">
          {t("pages.customers.didsEmpty")}
        </div>
      ) : (
        <ul className="space-y-1">
          {dids.map((d) => {
            const isDefault = normalizeDigits(defaultCid) === normalizeDigits(d);
            return (
              <li
                key={d}
                className={`flex items-center justify-between gap-2 rounded px-2 py-1 text-sm ${
                  isDefault ? "bg-[hsl(var(--accent))]/10" : "bg-muted/40"
                }`}
                data-testid={`did-row-${normalizeDigits(d)}`}
              >
                <div className="flex items-center gap-2 font-mono text-xs">
                  {isDefault ? (
                    <Star className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
                  ) : (
                    <StarOff className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <span>{d}</span>
                  {isDefault && (
                    <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--accent))]">
                      {t("pages.customers.didDefault")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setDefault(d)}
                    title={
                      isDefault
                        ? t("pages.customers.didClearDefault")
                        : t("pages.customers.didSetDefault")
                    }
                    data-testid={`did-toggle-default-${normalizeDigits(d)}`}
                  >
                    {isDefault
                      ? t("pages.customers.didClearDefaultBtn")
                      : t("pages.customers.didSetDefaultBtn")}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => remove(d)}
                    className="text-[hsl(var(--danger))]"
                    data-testid={`did-remove-${normalizeDigits(d)}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * OutboundTrunksPicker — Phase 6i.
 *
 * Operator picks an ordered list of trunks the customer's outbound
 * calls go through. When non-empty:
 *   - Engine bypasses the global Routing table.
 *   - Engine bypasses dial-plan transforms (prefix_strip / prefix_add).
 *   - The customer's PBX dials destination digits as-is.
 * The list defines failover order: primary first, then fallbacks.
 *
 * UX is deliberately simple (Add / Remove / ↑ / ↓) so it works on
 * iPad without HTML5 drag-and-drop. Trunks already in the list are
 * filtered out of the "Add" dropdown to keep selection unambiguous.
 */
export function OutboundTrunksPicker({ value = [], onChange }) {
  const { t } = useTranslation();
  const [trunks, setTrunks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [pick, setPick] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/trunks").then((r) => {
      if (cancelled) return;
      setTrunks(r.data || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
    return () => { cancelled = true; };
  }, []);

  const byId = Object.fromEntries(trunks.map((t) => [t.id, t]));
  const remaining = trunks.filter((t) => !value.includes(t.id));

  const add = () => {
    if (!pick) return;
    onChange && onChange([...(value || []), pick]);
    setPick("");
  };
  const remove = (idx) => {
    const next = (value || []).slice();
    next.splice(idx, 1);
    onChange && onChange(next);
  };
  const move = (idx, delta) => {
    const next = (value || []).slice();
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange && onChange(next);
  };

  return (
    <div className="space-y-2" data-testid="outbound-trunks-picker">
      <Label className="flex items-center gap-1.5">
        {t("components.outboundTrunks.label")}
        <Info className="h-3.5 w-3.5 text-muted-foreground" />
      </Label>
      <p className="text-xs text-muted-foreground">
        {t("components.outboundTrunks.help")}
      </p>
      {(value || []).length === 0 ? (
        <div className="text-xs text-muted-foreground italic px-2 py-1.5 rounded border border-dashed border-border">
          {t("components.outboundTrunks.empty")}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {(value || []).map((tid, idx) => {
            const tr = byId[tid];
            return (
              <li
                key={tid}
                className="flex items-center justify-between rounded border border-border bg-card px-2.5 py-1.5"
                data-testid={`outbound-trunk-row-${idx}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] font-mono text-muted-foreground w-5 text-right">
                    {idx === 0 ? "1°" : `${idx + 1}°`}
                  </span>
                  <span className="text-sm font-medium truncate" title={tid}>
                    {tr?.name || `${tid.slice(0, 10)}…`}
                  </span>
                  {tr?.host && (
                    <span className="text-[11px] text-muted-foreground font-mono truncate">
                      → {tr.host}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost" size="sm" type="button"
                    disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    data-testid={`outbound-trunk-up-${idx}`}
                    aria-label={t("components.outboundTrunks.moveUp")}
                    title={t("components.outboundTrunks.moveUp")}
                    className="h-7 w-7 p-0"
                  >↑</Button>
                  <Button
                    variant="ghost" size="sm" type="button"
                    disabled={idx === (value || []).length - 1}
                    onClick={() => move(idx, 1)}
                    data-testid={`outbound-trunk-down-${idx}`}
                    aria-label={t("components.outboundTrunks.moveDown")}
                    title={t("components.outboundTrunks.moveDown")}
                    className="h-7 w-7 p-0"
                  >↓</Button>
                  <Button
                    variant="ghost" size="sm" type="button"
                    onClick={() => remove(idx)}
                    data-testid={`outbound-trunk-remove-${idx}`}
                    aria-label={t("components.outboundTrunks.remove")}
                    title={t("components.outboundTrunks.remove")}
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex items-center gap-2 pt-1">
        <select
          className="flex-1 rounded border border-input bg-background h-9 px-2 text-sm"
          value={pick}
          onChange={(e) => setPick(e.target.value)}
          disabled={!loaded || remaining.length === 0}
          data-testid="outbound-trunk-picker-select"
        >
          <option value="">
            {loaded
              ? remaining.length === 0
                ? t("components.outboundTrunks.pickAllAdded")
                : t("components.outboundTrunks.pickPlaceholder")
              : t("components.outboundTrunks.pickLoading")}
          </option>
          {remaining.map((tr) => (
            <option key={tr.id} value={tr.id}>
              {tr.name || tr.id} {tr.host ? `(${tr.host})` : ""}
            </option>
          ))}
        </select>
        <Button
          type="button" size="sm"
          disabled={!pick}
          onClick={add}
          data-testid="outbound-trunk-add-btn"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> {t("components.outboundTrunks.add")}
        </Button>
      </div>
    </div>
  );
}

/**
 * useTenantForm — shared state + save logic for the TenantDialog and the
 * new TenantInlineEditor (Phase N+ "3CX/Vodia-style" full-page editor).
 * Returns `{form, setForm, save, platformMaxCD, allowedIpsStr}`.
 *
 * - `ready` controls whether form init fires (true = init now). For the
 *   dialog we pass `open`; for the inline editor we pass `true` from
 *   mount so it's ready immediately.
 */
function useTenantForm({ ready, tenant, onSaved, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [platformMaxCD, setPlatformMaxCD] = useState(null);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    api.get("/platform/settings")
      .then((r) => {
        if (!cancelled) {
          setPlatformMaxCD(
            typeof r.data?.max_call_duration === "number"
              ? r.data.max_call_duration
              : null,
          );
        }
      })
      .catch(() => {
        /* tenant-scope users can't read it; that's fine */
      });
    return () => { cancelled = true; };
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    // Phase 6o Hotfix H — the Provider / Reseller / Customer 3-tier
    // hierarchy is retired. Every tenant-level party is a plain
    // "customer" now, so the form init has no `kind` or `parent_id`
    // branching left. Keeping an explicit `kind: "customer"` on the
    // wire so older backends that still validate the field keep
    // working; the server-side migration renormalises everything on
    // boot anyway.
    setForm(
      tenant
        ? { ...tenant }
        : {
            kind: "customer",
            name: "",
            domain: "",
            business_type: "",
            limits: { channels: 0, cps: 0 },
            enabled: true,
            dids: [],
            default_cid: "",
            passthrough_far_end_cid: false,
            outbound_trunks: [],
            max_call_duration: null,
            auth: { mode: "digest", username: "", password: "", allowed_ips: [] },
          }
    );
  }, [ready, tenant]);

  const save = async () => {
    try {
      const body = { ...form };
      body.kind = "customer";
      // Phase 6o Hotfix H — parent_id is no longer meaningful; scrub
      // it from any legacy record being re-saved so stale values don't
      // leak back into the DB.
      delete body.parent_id;
      body.limits = {
        channels: Number(body?.limits?.channels || 0),
        cps: Number(body?.limits?.cps || 0),
        channels_in: Number(body?.limits?.channels_in || 0),
        channels_out: Number(body?.limits?.channels_out || 0),
      };
      body.channel_policy = body.channel_policy || "total";
      body.reject_mode = body.reject_mode || "plain";
      if (body.max_call_duration === "" || body.max_call_duration === undefined) {
        body.max_call_duration = null;
      } else if (body.max_call_duration !== null) {
        const n = parseInt(body.max_call_duration, 10);
        body.max_call_duration = Number.isFinite(n) ? Math.max(0, Math.min(86400, n)) : null;
      }
      if (body.auth && typeof body.auth.allowed_ips === "string") {
        body.auth.allowed_ips = body.auth.allowed_ips
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      if (tenant?.id) {
        await api.put(`/tenants/${tenant.id}`, body);
      } else {
        await api.post("/tenants", body);
      }
      toast.success(t("common.saved"));
      onClose && onClose();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.saveFailed"));
    }
  };

  const allowedIpsStr = Array.isArray(form?.auth?.allowed_ips)
    ? form.auth.allowed_ips.join(", ")
    : form?.auth?.allowed_ips || "";

  return { form, setForm, save, platformMaxCD, allowedIpsStr };
}

/**
 * TenantFormFields — the actual form, decoupled from its shell (dialog vs
 * inline). Used by both TenantDialog and TenantInlineEditor so the two
 * stay in lock-step. Phase 6o Hotfix H: kind/parents props retired.
 */
function TenantFormFields({ form, setForm, platformMaxCD, allowedIpsStr }) {
  const { t } = useTranslation();
  return (
    <>
      <div>
        <Label>{t("fields.name")}</Label>
        <Input
          data-testid="tenant-name"
          value={form.name || ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>
      {/* Phase 6o Hotfix H — parent_id select retired (no provider /
          reseller tier anymore). */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>{t("fields.channels")}</Label>
          <Input
            type="number"
            min={0}
            data-testid="tenant-channels"
            value={form.limits?.channels ?? 0}
            onChange={(e) =>
              setForm({ ...form, limits: { ...form.limits, channels: e.target.value } })
            }
          />
        </div>
        <div>
          <Label>{t("fields.cps")}</Label>
          <Input
            type="number"
            min={0}
            data-testid="tenant-cps"
            value={form.limits?.cps ?? 0}
            onChange={(e) =>
              setForm({ ...form, limits: { ...form.limits, cps: e.target.value } })
            }
          />
        </div>
      </div>
      <div>
        <Label>{t("fields.channelPolicy")}</Label>
        <select
          data-testid="tenant-channel-policy"
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={form.channel_policy || "total"}
          onChange={(e) => setForm({ ...form, channel_policy: e.target.value })}
        >
          <option value="total">{t("fields.channelPolicy.total")}</option>
          <option value="split">{t("fields.channelPolicy.split")}</option>
          <option value="both">{t("fields.channelPolicy.both")}</option>
        </select>
      </div>
      {(form.channel_policy === "split" || form.channel_policy === "both") && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t("fields.channelsIn")}</Label>
            <Input
              type="number"
              min={0}
              data-testid="tenant-channels-in"
              value={form.limits?.channels_in ?? 0}
              onChange={(e) =>
                setForm({ ...form, limits: { ...form.limits, channels_in: e.target.value } })
              }
            />
          </div>
          <div>
            <Label>{t("fields.channelsOut")}</Label>
            <Input
              type="number"
              min={0}
              data-testid="tenant-channels-out"
              value={form.limits?.channels_out ?? 0}
              onChange={(e) =>
                setForm({ ...form, limits: { ...form.limits, channels_out: e.target.value } })
              }
            />
          </div>
        </div>
      )}
      {/* Phase 6o Hotfix H — reject mode always shown (was gated on
          kind customer|reseller; now every tenant IS a customer). */}
      <div>
        <Label>{t("fields.rejectMode")}</Label>
        <select
          data-testid="tenant-reject-mode"
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={form.reject_mode || "plain"}
          onChange={(e) => setForm({ ...form, reject_mode: e.target.value })}
        >
          <option value="plain">{t("fields.rejectMode.plain")}</option>
          <option value="verbose">{t("fields.rejectMode.verbose")}</option>
        </select>
      </div>
      <div data-testid="tenant-max-call-duration-row">
          <Label>Max call duration (seconds)</Label>
          <NumberInput
            min={0}
            max={86400}
            allowEmpty
            placeholder={
              platformMaxCD === null
                ? "Use platform default"
                : `Use platform default (${platformMaxCD}s)`
            }
            value={
              form.max_call_duration === null ||
              form.max_call_duration === undefined ||
              form.max_call_duration === ""
                ? null
                : Number(form.max_call_duration)
            }
            onChange={(n) => setForm({ ...form, max_call_duration: n })}
            data-testid="tenant-max-call-duration"
          />
          <p className="text-[11px] text-muted-foreground mt-1 flex items-start gap-1">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            {platformMaxCD === null ? (
              <span>
                Leave empty to use the platform default. <b>0</b> = unlimited.
              </span>
            ) : (
              <span>
                Platform default is{" "}
                <span className="font-mono text-foreground">{platformMaxCD}s</span>{" "}
                {platformMaxCD === 0 ? "(unlimited)" : `(~${Math.floor(platformMaxCD / 60)}m)`}.
                Leave empty to inherit it. <b>0</b> = unlimited for this customer.
              </span>
            )}
          </p>
        </div>
      {/* Phase 6o Hotfix H — domain, auth, codecs, dids, outbound trunks
          are always shown; the only tenant kind is "customer" now. */}
      <div>
            <Label>{t("fields.domain")}</Label>
            <Input
              data-testid="tenant-domain"
              placeholder="customer1.elswitch.local"
              value={form.domain || ""}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
            />
          </div>
          {/* Phase 6o Hotfix I — Business / Category type. Free-text
              with autocomplete from existing values across the org. */}
          <div>
            <Label>{t("fields.businessType", "Business type")}</Label>
            <BusinessTypeCombobox
              value={form.business_type || ""}
              onChange={(v) => setForm({ ...form, business_type: v })}
              testid="tenant-business-type"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t(
                "pages.customers.help.businessType",
                "Used for filtering and grouping customers in the list. Optional."
              )}
            </p>
          </div>
          <div>
            <Label>{t("fields.authMode")}</Label>
            <select
              data-testid="tenant-authmode"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.auth?.mode || "digest"}
              onChange={(e) =>
                setForm({ ...form, auth: { ...form.auth, mode: e.target.value } })
              }
            >
              <option value="digest">{t("fields.authDigest")}</option>
              <option value="ip">{t("fields.authIp")}</option>
            </select>
          </div>
          {form.auth?.mode === "digest" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("fields.sipUsername")}</Label>
                <Input
                  data-testid="tenant-authuser"
                  value={form.auth?.username || ""}
                  onChange={(e) =>
                    setForm({ ...form, auth: { ...form.auth, username: e.target.value } })
                  }
                />
              </div>
              <div>
                <Label>{t("fields.sipPassword")}</Label>
                <PasswordInput
                  data-testid="tenant-authpass"
                  value={form.auth?.password || ""}
                  onChange={(e) =>
                    setForm({ ...form, auth: { ...form.auth, password: e.target.value } })
                  }
                />
              </div>
            </div>
          )}
          <div>
            <Label>{t("fields.allowedIps")}</Label>
            <Input
              data-testid="tenant-allowed-ips"
              value={allowedIpsStr}
              onChange={(e) =>
                setForm({ ...form, auth: { ...form.auth, allowed_ips: e.target.value } })
              }
            />
          </div>
          <CodecPolicyEditor
            mode={form.codec_policy_mode}
            codecs={form.codec_policy}
            onChange={({ mode, codecs }) =>
              setForm({ ...form, codec_policy_mode: mode, codec_policy: codecs })
            }
            idPrefix="customer-codec"
          />
          <DidsEditor
            dids={form.dids || []}
            defaultCid={form.default_cid || ""}
            passthroughFarEndCid={!!form.passthrough_far_end_cid}
            onChange={({ dids, default_cid, passthrough_far_end_cid }) =>
              setForm({ ...form, dids, default_cid, passthrough_far_end_cid })
            }
          />
          <OutboundTrunksPicker
            value={form.outbound_trunks || []}
            onChange={(next) => setForm({ ...form, outbound_trunks: next })}
          />
    </>
  );
}

export function TenantDialog({ open, onOpenChange, tenant, onSaved }) {
  const { t } = useTranslation();
  const { form, setForm, save, platformMaxCD, allowedIpsStr } = useTenantForm({
    ready: open, tenant, onSaved,
    onClose: () => onOpenChange(false),
  });
  const dialogTitle = () =>
    tenant ? t("pages.customers.dialogEdit") : t("pages.customers.dialogNew");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col p-0"
        data-testid="tenant-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-border">
          <DialogTitle>{dialogTitle()}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 overflow-y-auto px-6 py-4 flex-1 min-h-0">
          <TenantFormFields
            form={form} setForm={setForm}
            platformMaxCD={platformMaxCD} allowedIpsStr={allowedIpsStr}
          />
        </div>
        <DialogFooter className="px-6 py-4 border-t border-border shrink-0 bg-background">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={save} data-testid="tenant-save">
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * CustomerTabbedFields — Phase 6m.
 *
 * 3CX/Vodia-flavored tabbed layout for the customer inline editor.
 * Splits the formerly-scrolling single form into six purpose-named
 * tabs (General / Channels / SIP Auth / Codecs / DIDs & CIDs /
 * Outbound Trunks) that mirror the VoipMS trunk editor we saw in
 * IMG_0282 — operators coming from 3CX / Vodia will recognize the IA
 * instantly. Section headings inside each tab are the Vodia-style
 * small-caps ruled chunks. The two-column grid on md+ is the
 * Vodia "Account Details / Server Details" pattern.
 *
 * The dialog-shell path (`TenantFormFields` below) is intentionally
 * left untouched — it stays flat for non-customer kinds
 * (providers/resellers) and for the legacy TenantDialog fallback so we
 * don't regress those surfaces.
 */
function CustomerTabbedFields({ form, setForm, platformMaxCD, allowedIpsStr }) {
  const { t } = useTranslation();
  return (
    <Tabs
      defaultValue="general"
      className="w-full"
      data-testid="customer-editor-tabs"
    >
      {/* Phase 6n-g — IMPORTANT: TabsList is NOT sticky.
          Sticky-inside-overflow-y-auto (the master-detail
          ResizablePanel) misbehaves on iPad Safari, causing the
          TabsList to escape its DOM position and render BELOW the
          first SectionHeading inside TabsContent. Operators saw
          "ACCOUNT DETAILS" / "CHANNEL LIMITS" / "DIDS & CALLER ID"
          floating above the tabs row with a large whitespace gap
          (IMG_0294..0298). The action bar above is the only sticky
          surface we actually need — Save/Cancel stays reachable
          while the tabs flow naturally. */}
      <TabsList
        className="w-full justify-start gap-1 overflow-x-auto rounded-none border-b bg-card px-2 py-1 h-auto"
      >
        <TabsTrigger
          value="general"
          data-testid="customer-tab-general"
          className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none"
        >
          {t("pages.customers.tabs.general", "General")}
        </TabsTrigger>
        <TabsTrigger
          value="channels"
          data-testid="customer-tab-channels"
          className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none"
        >
          {t("pages.customers.tabs.channels", "Channels")}
        </TabsTrigger>
        <TabsTrigger
          value="sip"
          data-testid="customer-tab-sip"
          className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none"
        >
          {t("pages.customers.tabs.sipAuth", "SIP Auth")}
        </TabsTrigger>
        <TabsTrigger
          value="codecs"
          data-testid="customer-tab-codecs"
          className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none"
        >
          {t("pages.customers.tabs.codecs", "Codecs")}
        </TabsTrigger>
        <TabsTrigger
          value="dids"
          data-testid="customer-tab-dids"
          className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none"
        >
          {t("pages.customers.tabs.didsCids", "DIDs & CIDs")}
        </TabsTrigger>
        <TabsTrigger
          value="outbound"
          data-testid="customer-tab-outbound"
          className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none"
        >
          {t("pages.customers.tabs.outboundTrunks", "Outbound Trunks")}
        </TabsTrigger>
      </TabsList>

      {/* GENERAL — identity, domain, max call duration. */}
      <TabsContent value="general" className="p-4 md:p-5 space-y-5">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12">
            <SectionHeading testid="section-account-details">
              {t("pages.customers.sections.accountDetails", "Account Details")}
            </SectionHeading>
            <Separator className="my-3" />
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label htmlFor="c-name" className="text-xs text-muted-foreground">
              {t("fields.name")}
            </Label>
            <Input
              id="c-name"
              data-testid="tenant-name"
              value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t("pages.customers.help.name", "Shown in Live Traffic, CDRs and exports.")}
            </p>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label htmlFor="c-domain" className="text-xs text-muted-foreground">
              {t("fields.domain")}
            </Label>
            <Input
              id="c-domain"
              data-testid="tenant-domain"
              placeholder="customer1.elswitch.local"
              value={form.domain || ""}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              className="mt-1 font-mono"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t("pages.customers.help.domain", "SIP domain this user registers from. Optional.")}
            </p>
          </div>
          {/* Phase 6o Hotfix I — Business type field with autocomplete.
              Lives on the same row as Domain so the identity
              fields (name / domain / business-type) line up. */}
          <div className="col-span-12 md:col-span-6">
            <Label className="text-xs text-muted-foreground">
              {t("fields.businessType", "Business type")}
            </Label>
            <div className="mt-1">
              <BusinessTypeCombobox
                value={form.business_type || ""}
                onChange={(v) => setForm({ ...form, business_type: v })}
                testid="tenant-business-type"
              />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t(
                "pages.customers.help.businessType",
                "Used for filtering and grouping customers in the list. Optional."
              )}
            </p>
          </div>
          <div className="col-span-12 md:col-span-6" data-testid="tenant-max-call-duration-row">
            <Label className="text-xs text-muted-foreground">
              {t("pages.customers.fields.maxCallDuration", "Max call duration (seconds)")}
            </Label>
            <NumberInput
              min={0}
              max={86400}
              allowEmpty
              placeholder={
                platformMaxCD === null
                  ? t("pages.customers.fields.usePlatformDefault", "Use platform default")
                  : `${t("pages.customers.fields.usePlatformDefault", "Use platform default")} (${platformMaxCD}s)`
              }
              value={
                form.max_call_duration === null ||
                form.max_call_duration === undefined ||
                form.max_call_duration === ""
                  ? null
                  : Number(form.max_call_duration)
              }
              onChange={(n) => setForm({ ...form, max_call_duration: n })}
              data-testid="tenant-max-call-duration"
            />
            <p className="mt-1 text-[11px] text-muted-foreground flex items-start gap-1">
              <Info className="h-3 w-3 mt-0.5 shrink-0" />
              {platformMaxCD === null ? (
                <span>
                  {t("pages.customers.help.maxCallDurationEmpty", "Leave empty to use the platform default.")} <b>0</b> = {t("pages.customers.help.unlimited", "unlimited")}.
                </span>
              ) : (
                <span>
                  {t("pages.customers.help.platformDefault", "Platform default is")}{" "}
                  <span className="font-mono text-foreground">{platformMaxCD}s</span>.
                  <b> 0</b> = {t("pages.customers.help.unlimited", "unlimited")}.
                </span>
              )}
            </p>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label className="text-xs text-muted-foreground">{t("fields.rejectMode")}</Label>
            <select
              data-testid="tenant-reject-mode"
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.reject_mode || "plain"}
              onChange={(e) => setForm({ ...form, reject_mode: e.target.value })}
            >
              <option value="plain">{t("fields.rejectMode.plain")}</option>
              <option value="verbose">{t("fields.rejectMode.verbose")}</option>
            </select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t("pages.customers.help.rejectMode", "Verbose adds diagnostic reason to rejection responses.")}
            </p>
          </div>
        </div>
      </TabsContent>

      {/* CHANNELS — concurrency limits + policy. */}
      <TabsContent value="channels" className="p-4 md:p-5 space-y-5">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12">
            <SectionHeading testid="section-channel-limits">
              {t("pages.customers.sections.channelLimits", "Channel Limits")}
            </SectionHeading>
            <Separator className="my-3" />
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label className="text-xs text-muted-foreground">{t("fields.channels")}</Label>
            <Input
              type="number" min={0}
              data-testid="tenant-channels"
              className="mt-1 font-mono"
              value={form.limits?.channels ?? 0}
              onChange={(e) =>
                setForm({ ...form, limits: { ...form.limits, channels: e.target.value } })
              }
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t("pages.customers.help.channels", "Max concurrent calls. 0 = unlimited.")}
            </p>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label className="text-xs text-muted-foreground">{t("fields.cps")}</Label>
            <Input
              type="number" min={0}
              data-testid="tenant-cps"
              className="mt-1 font-mono"
              value={form.limits?.cps ?? 0}
              onChange={(e) =>
                setForm({ ...form, limits: { ...form.limits, cps: e.target.value } })
              }
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t("pages.customers.help.cps", "Calls per second limit. 0 = unlimited.")}
            </p>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label className="text-xs text-muted-foreground">{t("fields.channelPolicy")}</Label>
            <select
              data-testid="tenant-channel-policy"
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.channel_policy || "total"}
              onChange={(e) => setForm({ ...form, channel_policy: e.target.value })}
            >
              <option value="total">{t("fields.channelPolicy.total")}</option>
              <option value="split">{t("fields.channelPolicy.split")}</option>
              <option value="both">{t("fields.channelPolicy.both")}</option>
            </select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t("pages.customers.help.channelPolicy", "Total = one pool. Split = separate in/out. Both = enforce total AND split.")}
            </p>
          </div>
          {(form.channel_policy === "split" || form.channel_policy === "both") && (
            <>
              <div className="col-span-12 md:col-span-6">
                <Label className="text-xs text-muted-foreground">{t("fields.channelsIn")}</Label>
                <Input
                  type="number" min={0}
                  data-testid="tenant-channels-in"
                  className="mt-1 font-mono"
                  value={form.limits?.channels_in ?? 0}
                  onChange={(e) =>
                    setForm({ ...form, limits: { ...form.limits, channels_in: e.target.value } })
                  }
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Label className="text-xs text-muted-foreground">{t("fields.channelsOut")}</Label>
                <Input
                  type="number" min={0}
                  data-testid="tenant-channels-out"
                  className="mt-1 font-mono"
                  value={form.limits?.channels_out ?? 0}
                  onChange={(e) =>
                    setForm({ ...form, limits: { ...form.limits, channels_out: e.target.value } })
                  }
                />
              </div>
            </>
          )}
        </div>
      </TabsContent>

      {/* SIP AUTH — digest vs IP mode, credentials, allowed IPs. */}
      <TabsContent value="sip" className="p-4 md:p-5 space-y-5">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12">
            <SectionHeading testid="section-sip-authentication">
              {t("pages.customers.sections.sipAuthentication", "SIP Authentication")}
            </SectionHeading>
            <Separator className="my-3" />
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label className="text-xs text-muted-foreground">{t("fields.authMode")}</Label>
            <select
              data-testid="tenant-authmode"
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.auth?.mode || "digest"}
              onChange={(e) =>
                setForm({ ...form, auth: { ...form.auth, mode: e.target.value } })
              }
            >
              <option value="digest">{t("fields.authDigest")}</option>
              <option value="ip">{t("fields.authIp")}</option>
            </select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t("pages.customers.help.authMode", "Digest: username + password. IP: trust by source IP.")}
            </p>
          </div>
          {form.auth?.mode === "digest" && (
            <>
              <div className="col-span-12 md:col-span-6">
                <Label className="text-xs text-muted-foreground">{t("fields.sipUsername")}</Label>
                <Input
                  data-testid="tenant-authuser"
                  className="mt-1 font-mono"
                  value={form.auth?.username || ""}
                  onChange={(e) =>
                    setForm({ ...form, auth: { ...form.auth, username: e.target.value } })
                  }
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Label className="text-xs text-muted-foreground">{t("fields.sipPassword")}</Label>
                <PasswordInput
                  data-testid="tenant-authpass"
                  className="mt-1 font-mono"
                  value={form.auth?.password || ""}
                  onChange={(e) =>
                    setForm({ ...form, auth: { ...form.auth, password: e.target.value } })
                  }
                />
              </div>
            </>
          )}
          <div className="col-span-12">
            <Label className="text-xs text-muted-foreground">{t("fields.allowedIps")}</Label>
            <Input
              data-testid="tenant-allowed-ips"
              className="mt-1 font-mono"
              value={allowedIpsStr}
              onChange={(e) =>
                setForm({ ...form, auth: { ...form.auth, allowed_ips: e.target.value } })
              }
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t("pages.customers.help.allowedIps", "Comma-separated IPs / CIDRs allowed to register / call. Empty = any.")}
            </p>
          </div>
        </div>
      </TabsContent>

      {/* CODECS — encoder allow/deny list. */}
      <TabsContent value="codecs" className="p-4 md:p-5 space-y-5">
        <SectionHeading testid="section-codec-policy">
          {t("pages.customers.sections.codecPolicy", "Codec Policy")}
        </SectionHeading>
        <Separator className="my-3" />
        <CodecPolicyEditor
          mode={form.codec_policy_mode}
          codecs={form.codec_policy}
          onChange={({ mode, codecs }) =>
            setForm({ ...form, codec_policy_mode: mode, codec_policy: codecs })
          }
          idPrefix="customer-codec"
        />
      </TabsContent>

      {/* DIDs & CIDs — inventory + default outbound caller-id. */}
      <TabsContent value="dids" className="p-4 md:p-5 space-y-5">
        <SectionHeading testid="section-dids-cids">
          {t("pages.customers.sections.didsCids", "DIDs & Caller ID")}
        </SectionHeading>
        <Separator className="my-3" />
        <DidsEditor
          dids={form.dids || []}
          defaultCid={form.default_cid || ""}
          passthroughFarEndCid={!!form.passthrough_far_end_cid}
          onChange={({ dids, default_cid, passthrough_far_end_cid }) =>
            setForm({ ...form, dids, default_cid, passthrough_far_end_cid })
          }
        />
      </TabsContent>

      {/* OUTBOUND TRUNKS — Phase 6i per-user trunk binding. */}
      <TabsContent value="outbound" className="p-4 md:p-5 space-y-5">
        <SectionHeading testid="section-outbound-trunks">
          {t("pages.customers.sections.outboundTrunks", "Outbound Trunk Binding")}
        </SectionHeading>
        <Separator className="my-3" />
        <OutboundTrunksPicker
          value={form.outbound_trunks || []}
          onChange={(next) => setForm({ ...form, outbound_trunks: next })}
        />
      </TabsContent>
    </Tabs>
  );
}

/**
 * TenantInlineEditor — full-page "3CX/Vodia-style" editor. No modal,
 * no overlay. Breadcrumb + Save/Cancel at the top. The page itself
 * becomes the editor, matching the pattern the operator asked for on
 * iPad where floating modals fight the on-screen keyboard.
 *
 * Phase 6m — for `kind === "customer"` the body is now a 6-tab
 * 3CX/Vodia-flavored editor (see CustomerTabbedFields). Other kinds
 * (provider, reseller) keep the flat scrolling form to avoid
 * regressing their simpler field set.
 */
export function TenantInlineEditor({ tenant, onCancel, onSaved }) {
  const { t } = useTranslation();
  const { form, setForm, save, platformMaxCD, allowedIpsStr } = useTenantForm({
    ready: true, tenant, onSaved, onClose: onCancel,
  });
  const title = tenant ? t("pages.customers.dialogEdit") : t("pages.customers.dialogNew");
  const listLabel = t("nav.customers", "Customers");
  const enabled = form.enabled !== false;
  return (
    <div data-testid="tenant-inline-editor">
      {/* Sticky action bar — breadcrumb + status square + Save/Cancel.
          NOTE (Phase 6n-g hotfix): the inner Tabs list is INTENTIONALLY
          NOT sticky. Sticky-inside-overflow on iPad Safari caused the
          TabsList to leak below SectionHeading (IMG_0294..0298). This
          action bar is the only sticky surface in the editor. */}
      <div className="sticky top-[var(--toolbar-h)] z-30 -mx-3 sm:-mx-4 md:-mx-6 px-3 sm:px-4 md:px-6 py-3 bg-card border-b border-border flex items-center gap-3 flex-wrap shadow-sm">
        <nav className="text-xs text-muted-foreground flex items-center gap-1.5 flex-1 min-w-0">
          <button
            type="button"
            onClick={onCancel}
            className="hover:text-foreground transition-colors"
            data-testid="tenant-inline-back"
          >
            {listLabel}
          </button>
          <span>/</span>
          <span className="text-foreground font-medium truncate flex items-center gap-2">
            {tenant && (
              <StatusSquare
                kind={enabled ? "ok" : "offline"}
                title={enabled ? t("common.enabled", "Enabled") : t("common.disabled", "Disabled")}
              />
            )}
            {tenant ? tenant.name : t("common.new", "New")}
          </span>
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" onClick={onCancel} data-testid="tenant-inline-cancel">
            {t("common.cancel")}
          </Button>
          <Button onClick={save} data-testid="tenant-inline-save">
            {t("common.save")}
          </Button>
        </div>
      </div>

      {/* Phase 6o Hotfix H — always render the 3CX/Vodia tabbed layout.
          The legacy "flat form for providers/resellers" branch is gone
          because those tenant kinds are retired. */}
      <div className="mx-auto max-w-5xl py-4 px-0">
        <h1 className="text-xl font-semibold mb-3 px-3 md:px-0">{title}</h1>
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <CustomerTabbedFields
            form={form}
            setForm={setForm}
            platformMaxCD={platformMaxCD}
            allowedIpsStr={allowedIpsStr}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * useTenants — small hook for fetching the tenant list.
 * Phase 6o Hotfix H: `kind` argument is ignored (every tenant is a
 * customer now). The signature is kept so existing callers don't have
 * to change in the same commit; the backend discards the query arg.
 */
export function useTenants(_kind) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api
      .get(`/tenants`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { items, loading, reload: load, setItems };
}

// parentKindFor retired (no more parent tiers).
